import { Award, MapPin, ShieldCheck, Users } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Chapter } from "@/components/Reveal";
import { CTARow } from "@/components/CTA";
import { SITE, IMAGES } from "@/data/site";

const POINTS = [
  { icon: Award, title: "20+ Years of Experience", desc: "Assisting U.S. travelers and businesses with travel documents." },
  { icon: MapPin, title: "Washington, DC Office", desc: `${SITE.addressLine1}, ${SITE.addressLine2}.` },
  { icon: ShieldCheck, title: "Private Service Provider", desc: "Not a government agency — transparent about who we are." },
  { icon: Users, title: "Individuals & Businesses", desc: "From family vacations to corporate travel programs." },
];

export default function AboutPage() {
  return (
    <>
      <Seo
        title="About Us — 20+ Years of Travel Document Assistance | Travel Passport and Visa LLC"
        siteName={SITE.siteName}
        description="Travel Passport and Visa LLC is a privately owned Washington, DC company with more than 20 years of experience assisting U.S. travelers and businesses with passports, visas, and travel documents."
      />
      <section className="grain bg-navy py-16 lg:py-24" data-testid="about-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <span className="inline-block rounded-full border border-gold/40 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-gold">
            Private Service Provider
          </span>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-bold tracking-tight text-white sm:text-5xl" data-testid="about-h1">
            About Travel Passport and Visa LLC
          </h1>
          <CTARow light className="mt-8" testPrefix="about" />
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl items-start gap-14 px-4 sm:px-6 lg:grid-cols-[1.1fr_0.9fr]">
          <Reveal>
            <Chapter num="01" label="Who We Are" />
            <div className="space-y-5 text-base leading-relaxed text-slate-600" data-testid="about-copy">
              <p className="text-lg text-slate-700">
                Travel Passport and Visa LLC is a privately owned company with more than <strong className="text-navy">20 years of experience</strong> assisting U.S. travelers and businesses with passport, visa, and travel document services.
              </p>
              <p>
                We help individuals, executives, employees, and organizations navigate documentation requirements associated with domestic and international travel. Our team provides professional assistance with passport applications, renewals, urgent travel needs, visas, and other travel document services.
              </p>
              <p>
                Travel Passport and Visa LLC works with applicable U.S. passport agencies and foreign consulates to assist clients with their documentation needs.
              </p>
              <p>
                Travel Passport and Visa LLC and Urgent Passport &amp; Visa operate under the same ownership, ensuring seamless and reliable service for all your travel document needs.
              </p>
            </div>
            <div className="mt-8 rounded-xl border-l-4 border-gold bg-slate-50 p-6" data-testid="about-disclosure-note">
              <p className="text-sm leading-relaxed text-slate-600">{SITE.disclosure}</p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -left-4 -top-4 h-24 w-24 border-l-2 border-t-2 border-gold" />
              <img
                src={IMAGES.dc}
                alt="Washington, DC, where Travel Passport and Visa LLC is headquartered"
                className="w-full rounded-2xl object-cover shadow-2xl shadow-navy/10"
                loading="lazy"
              />
            </div>
            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              {POINTS.map((p, i) => (
                <div key={p.title} className="rounded-xl border border-slate-200 p-5" data-testid={`about-point-${i}`}>
                  <p.icon className="h-5 w-5 text-gold-dark" />
                  <p className="mt-3 font-display text-sm font-bold text-navy">{p.title}</p>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">{p.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="grain bg-navy-deep py-16" data-testid="about-bottom-cta">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-5 px-4 text-center sm:px-6">
          <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">Ready to Get Started?</h2>
          <p className="max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base">
            Tell us about your travel plans and the documentation assistance you need.
          </p>
          <CTARow light testPrefix="about-bottom" />
        </div>
      </section>
    </>
  );
}
